import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { FeatureFlag } from '@/client/feature-flags/types';
import { TableNamesEnum } from '@/common/constants';
import { roleTranslationsByCode } from '@/common/constants/roles';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableToolbar,
} from '@/components/tables/crud';
import { useFeatureFlags } from '@/hooks/query/feature-flags.hooks';
import { useTable } from '@/hooks/table.hook';
import { useToast } from '@/hooks/useToast';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { ITEMS_PER_PAYLOAD } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FeatureFlagOverviewModal } from '../modals/FeatureFlagOverviewModal';
import { FeatureFlagRolesModal } from '../modals/FeatureFlagRolesModal';
import { FeatureFlagsModal } from '../modals/FeatureFlagsModal';

const StyledLink = styled.a`
  display: flex;
  justify-content: flex-start;
  min-width: max-content;
  text-decoration: none;
  color: var(--black-main);

  i {
    align-self: center;
  }

  &:hover {
    cursor: pointer;
    color: var(--red-dark);
  }
`;

export const FeatureFlagsDatatable: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { apiFilters } = useTable({ tableName: TableNamesEnum.FEATURES_LIST });

  const [isFeatureModalVisible, setIsFeatureModalVisible] = useState(false);
  const [
    isFeatureActivatedAccountsModalVisible,
    setIsFeatureActivatedAccountsModalVisible,
  ] = useState(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeatureFlag>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading, flags, refetch } = useFeatureFlags({
    filters: apiFilters,
  });

  const handleEnableDefaultFeatureFlag = async (
    feature: FeatureFlag,
    roles?: string[],
  ) => {
    const isEnabled = feature.defaultAccountSettings?.isEnabled;
    try {
      setIsSubmitting(true);

      await client.featureFlags.updateFeatureFlags(feature.id, {
        defaultAccountSettings: {
          isEnabled: feature.isRoleBased ? !!roles?.length : !isEnabled,
          isEnabledForFreeTrial: feature.isRoleBased
            ? !!roles?.length
            : !isEnabled,
          roles: roles ?? [],
        },
      });

      refetch();
      setIsRoleModalVisible(false);

      toast?.success(
        t('toast.success'),
        !isEnabled ? t('feature.defaultEnabled') : t('feature.defaultDisabled'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFeatureFlag = async (
    active: boolean,
    roles: string[],
    accounts: string[],
    productOwner?: string | null,
    documentationUrl?: string | null,
  ) => {
    try {
      if (!selectedFeature) return;

      setIsSubmitting(true);

      for (let index = 0; index < accounts.length; index += ITEMS_PER_PAYLOAD) {
        const accountsChunk = accounts.slice(index, index + ITEMS_PER_PAYLOAD);

        await client.featureFlags.updateFeatureFlags(selectedFeature.id, {
          accountMappings: accountsChunk?.length
            ? accountsChunk.map((id: string) => ({
                id,
                roles,
                active: active,
              }))
            : [],
          productOwner,
          documentationUrl,
        });
      }

      refetch();

      setIsFeatureModalVisible(false);

      toast?.success(t('toast.success'), t('feature.edit.success'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('feature.name'),
      sortable: false,
      filterable: false,
      render: (row: FeatureFlag) => <span>{t(`features.${row?.key}`)}</span>,
    },
    {
      field: 'key',
      header: t('feature.key'),
      sortable: false,
      filterable: false,
      render: (row: FeatureFlag) => <span>{row?.key}</span>,
    },
    {
      field: 'productOwner',
      header: t('feature.productOwner'),
      sortable: false,
      filterable: false,
      render: (row: FeatureFlag) =>
        row.productOwner ? (
          <span>{row?.productOwner}</span>
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'documentationUrl',
      header: t('feature.docUrl'),
      sortable: false,
      filterable: false,
      render: (row: FeatureFlag) =>
        row.documentationUrl ? (
          <StyledLink
            href={row?.documentationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div>{t('generic.link')}</div>
            <i className="pi pi-external-link x2 ml-2" />
          </StyledLink>
        ) : (
          <span>&#8212;</span>
        ),
    },
    {
      field: 'defaultAccountSettings.isEnabled',
      header: t('feature.defaultSettings'),
      style: { width: '380px' },
      sortable: false,
      filterable: false,
      render: (row: FeatureFlag) =>
        row.defaultAccountSettings?.isEnabled ? (
          <>
            <AppChip label={t('generic.enabled')} type="primary" size="s" />
            {!!row?.defaultAccountSettings?.roles?.length &&
              row.isRoleBased && (
                <div className="mt-2">
                  {row.defaultAccountSettings.roles
                    ?.map((role) => roleTranslationsByCode(role, t))
                    ?.join(', ')}
                </div>
              )}
          </>
        ) : (
          <AppChip label={t('generic.disabled')} type="secondary" size="s" />
        ),
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center',
      },
      render: (row: FeatureFlag) => (
        <DataTableActions menuItems={menuItems(row)} />
      ),
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const defaultVisibleColumns = columns.map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <div />
        <DataTableColumnsMultiselect
          columns={columns}
          tableName={TableNamesEnum.FEATURES_LIST}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          alwaysVisibleColumns={alwaysVisibleColumns}
        />
      </FlexContainer>
    </DataTableToolbar>
  );

  const menuItems = (feature: FeatureFlag) => {
    const menu: MenuItem[] = [
      {
        label: feature.isRoleBased
          ? t('feature.addAccountOrRole')
          : t('feature.addAccount'),
        icon: 'pi pi-plus',
        command: () => {
          setSelectedFeature(feature);
          setIsFeatureModalVisible(true);
        },
      },
      {
        label: t('feature.activated.accounts'),
        icon: 'pi pi-users',
        command: () => {
          setSelectedFeature(feature);
          setIsFeatureActivatedAccountsModalVisible(true);
        },
      },
      {
        label: feature.defaultAccountSettings?.isEnabled
          ? t('feature.disableByDefault')
          : t('feature.enableByDefault'),
        icon: feature.defaultAccountSettings?.isEnabled
          ? 'pi pi-times'
          : 'pi pi-check',
        command:
          !feature.isRoleBased || feature.defaultAccountSettings?.isEnabled
            ? async () => await handleEnableDefaultFeatureFlag(feature)
            : () => {
                setSelectedFeature(feature);
                setIsRoleModalVisible(true);
              },
      },
    ];

    if (feature.isRoleBased && feature.defaultAccountSettings?.isEnabled) {
      menu.push({
        label: t('feature.editDefaultRoles'),
        icon: 'pi pi-pencil',
        command: () => {
          setSelectedFeature(feature);
          setIsRoleModalVisible(true);
        },
      });
    }

    return menu;
  };

  return (
    <>
      <DataTable
        data={flags?.result}
        count={flags?.count}
        isLoading={isLoading}
        columns={columns}
        visibleColumns={visibleColumns}
        toolbar={toolbar}
      />

      <FeatureFlagsModal
        currentFeature={selectedFeature}
        visible={isFeatureModalVisible}
        isSubmitting={isSubmitting}
        onSubmit={handleUpdateFeatureFlag}
        onHide={() => setIsFeatureModalVisible(false)}
      />

      <FeatureFlagRolesModal
        currentFeature={selectedFeature}
        visible={isRoleModalVisible}
        isSubmitting={isSubmitting}
        onSubmit={handleEnableDefaultFeatureFlag}
        onHide={() => setIsRoleModalVisible(false)}
      />

      <FeatureFlagOverviewModal
        currentFeature={selectedFeature}
        visible={isFeatureActivatedAccountsModalVisible}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        onHide={() => setIsFeatureActivatedAccountsModalVisible(false)}
      />
    </>
  );
};
